import React, {useEffect, useMemo, useState} from 'react'
import {useQueries} from '@tanstack/react-query'
import Text from '../../components/text/Text'
import WizButton from '../../components/button/WizButton'
import {QueryCacheKeys} from '../../hooks/QueryKey'
import {getPromotionItems, IPromotionItem} from '../../apis/promotion/item'
import useQueryPromotionsByLang from '../../hooks/api/promotion/useQueryPromotionsByLang'
import useQueryPromotionItemsByLang from '../../hooks/api/promotion/useQueryPromotionItemsByLang'

interface IProps {
    open: boolean
    language: 'ko' | 'en' // 조회/적용 언어 (request header 강제)
    device: 'pc' | 'mo' // 추출 대상 device (banner=pc / mobile=mo)
    fieldLabel?: string // 타이틀 보조 라벨 (예: 'PC 배너')
    onSelect: (id: string) => void // 항목 클릭 → item id 적용
    onClose: () => void
}

/**
 * banner 가져오기 모달 — 2단계.
 *  ① active=false 프로모션 목록(지정 language) 중 tradermap-card(대상 device) 항목을 가진 것만 → 하나 선택
 *  ② 그 프로모션의 items 중 slot='tradermap-card' && device=대상 만 표시 → 클릭 시 id 적용
 * 언어는 버튼(ko/en)이 결정하며 admin 의 현재 언어 설정과 무관하게 조회된다.
 */
const BannerPickerModal: React.FC<IProps> = ({open, language, device, fieldLabel, onSelect, onClose}) => {
    const [promotionId, setPromotionId] = useState<string | null>(null)

    const {data: promotions, isLoading: loadingPromos} = useQueryPromotionsByLang(language, open)
    const {data: items, isLoading: loadingItems} = useQueryPromotionItemsByLang(promotionId, language, open)

    // 모달이 닫히면 선택 상태 초기화 (다음 오픈 시 항상 목록부터)
    useEffect(() => {
        if (!open) setPromotionId(null)
    }, [open])

    // /promotion 페이지(PromotionList)와 동일한 정렬:
    //  ① 아직 안 끝난(end_at > now) 프로모션 우선 → ② priority 내림차순 (id 15·16 은 priority 0 취급)
    const sortedPromotions = useMemo(() => {
        return [...(promotions || [])].sort((a, b) => {
            const now = new Date()
            const isFutureA = new Date(a?.end_at) > now
            const isFutureB = new Date(b?.end_at) > now
            const priorityA = a?.id === '15' || a?.id === '16' ? 0 : a?.priority
            const priorityB = b?.id === '15' || b?.id === '16' ? 0 : b?.priority
            if (isFutureA && !isFutureB) return -1
            if (!isFutureA && isFutureB) return 1
            return priorityB - priorityA
        })
    }, [promotions])

    // 각 프로모션의 items 를 병렬 조회 — step② 와 동일 캐시 키라 클릭 시 재요청 없음.
    // tradermap-card(대상 device) 항목 보유 여부 판별용.
    const itemQueries = useQueries({
        queries: (open ? sortedPromotions : []).map(p => ({
            queryKey: QueryCacheKeys.promotion.getPromotionItems(p?.id, language),
            queryFn: async () => {
                const {data} = await getPromotionItems(p?.id as string, language)
                return (data || []) as IPromotionItem[]
            },
            enabled: open && !!p?.id,
            refetchOnWindowFocus: false,
        })),
    })
    const itemsScanning = itemQueries.some(q => q.isLoading)

    // tradermap-card(대상 device) 항목을 가진 프로모션만 노출
    const visiblePromotions = sortedPromotions.filter((p, idx) => {
        const its = itemQueries[idx]?.data || []
        return its.some(it => it?.slot === 'tradermap-card' && it?.device === device)
    })

    if (!open) return null

    // slot=tradermap-card & 대상 device 항목은 리스트당 하나만 존재하도록 운영 → 첫 항목을 확정 대상으로 사용
    const targetItems = (items || []).filter(it => it?.slot === 'tradermap-card' && it?.device === device)
    const target = targetItems[0]

    return (
        <div className="fixed inset-0 z-modal flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative z-10 w-[90%] max-w-[560px] max-h-[80vh] overflow-y-auto bg-white rounded-[8px] p-6 shadow-hover">
                <div className="flex items-center justify-between gap-x-[10px]">
                    <Text className="text-h3 text-font01">
                        배너 가져오기{fieldLabel ? ` · ${fieldLabel}` : ''}
                        <span className="text-body3 text-font02 ml-[8px]">
                            ({language.toUpperCase()} / {device.toUpperCase()})
                        </span>
                    </Text>
                    <WizButton text={'닫기'} type="cancel" size="small" onClick={onClose} />
                </div>

                {promotionId === null ? (
                    /* ① 프로모션 목록 (tradermap-card 보유분만) */
                    <div className="mt-[16px]">
                        <Text className="text-body3 text-font02">
                            tradermap-card({device}) 항목이 있는 프로모션만 표시됩니다.
                        </Text>
                        {loadingPromos || itemsScanning ? (
                            <Text className="text-body2 text-font02 mt-[16px]">불러오는 중…</Text>
                        ) : (
                            <div className="mt-[12px] space-y-[8px]">
                                {visiblePromotions.map(p => (
                                    <div
                                        key={p?.id}
                                        onClick={() => setPromotionId(p?.id as string)}
                                        className="border border-line02 rounded-[6px] px-[14px] py-[10px] cursor-pointer hover:bg-bg_gray01 flex items-center gap-x-[10px]">
                                        {/* 상태 표시 — /promotion 리스트와 동일 기준 */}

                                        <Text className="text-body2 text-font01 font-bold truncate flex-1 min-w-0">
                                            {p?.name}
                                        </Text>
                                        {/* 언어 태그 — 가장 우측 */}
                                        <span className="shrink-0 text-ti3 font-bold text-font02 bg-bg_gray01 px-[8px] py-[2px] rounded-[4px] uppercase">
                                            {p?.language}
                                        </span>
                                    </div>
                                ))}
                                {visiblePromotions.length === 0 && (
                                    <Text className="text-body2 text-font02">
                                        tradermap-card({device}) 항목이 있는 프로모션이 없습니다.
                                    </Text>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    /* ② 선택한 프로모션의 대상 item 확정 (리스트당 1개) */
                    <div className="mt-[16px]">
                        {loadingItems ? (
                            <Text className="text-body2 text-font02">불러오는 중…</Text>
                        ) : !target ? (
                            <div>
                                <Text className="text-body2 text-font02">
                                    조건에 맞는 항목(tradermap-card · {device})이 없습니다.
                                </Text>
                                <div className="mt-[16px]">
                                    <WizButton text={'목록으로'} type="border" onClick={() => setPromotionId(null)} />
                                </div>
                            </div>
                        ) : (
                            <div>
                                {/* 배너 미리보기 — 실제 슬롯 카드와 동일 렌더 (자연 비율 + webp 최적화) */}
                                <div className="relative w-full max-w-[522px] rounded-[10px] overflow-hidden bg-gray-100 border border-line02">
                                    <img
                                        className="w-full h-auto object-cover"
                                        src={`${target.image_source}${
                                            target.image_source?.includes('?') ? '&' : '?'
                                        }type=webp&width=1044&quality=100`}
                                        alt="tradermap-card"
                                    />
                                    <div className="absolute top-2 left-2 z-20">
                                        <span className="bg-black/70 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm border border-white/10">
                                            Trader Map Top ({device === 'mo' ? 'MO' : 'PC'})
                                        </span>
                                    </div>
                                </div>
                                {/* id / url */}
                                <div className="mt-[14px] space-y-[6px]">
                                    <div className="flex items-center gap-x-[10px]">
                                        <Text className="text-body3 text-font02 w-[40px] shrink-0">id</Text>
                                        <Text className="text-body2 text-font01 font-bold">{target.id}</Text>
                                    </div>
                                    <div className="flex items-start gap-x-[10px]">
                                        <Text className="text-body3 text-font02 w-[40px] shrink-0">url</Text>
                                        <Text className="text-body3 text-font02 break-all">{target.url}</Text>
                                    </div>
                                </div>
                                {/* 확정 안내 + 버튼 */}
                                <Text className="text-body2 text-font01 font-bold mt-[18px]">
                                    해당 배너 id를 가져오시겠습니까?
                                </Text>
                                <div className="mt-[14px] flex items-center gap-x-[10px]">
                                    <WizButton
                                        text={'가져오기'}
                                        type="primary"
                                        onClick={() => {
                                            onSelect(String(target.id))
                                            onClose()
                                        }}
                                    />
                                    <WizButton
                                        text={'취소 (목록으로)'}
                                        type="cancel"
                                        onClick={() => setPromotionId(null)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default BannerPickerModal
